import { createWithEqualityFn } from 'zustand/traditional';

import type { InventoryPrintJob } from '../types';

const HIGHLIGHT_TTL_MS = 4_000;

type HighlightMap = Record<string, number>;
type ActiveMap = Record<string, true>;

interface InventoryPrintQueueLiveState {
  isConnected: boolean;
  highlightedJobs: HighlightMap;
  activeJobs: ActiveMap;
  activeCount: number;
  setConnected: (value: boolean) => void;
  syncActiveJobs: (jobs: InventoryPrintJob[]) => void;
  applyJobEvent: (job: InventoryPrintJob) => void;
  cleanupHighlights: () => void;
}

function isActiveStatus(status: InventoryPrintJob['status']): boolean {
  return status === 'queued' || status === 'processing';
}

export const useInventoryPrintQueueLiveStore = createWithEqualityFn<InventoryPrintQueueLiveState>()(
  (set) => ({
    isConnected: true,
    highlightedJobs: {},
    activeJobs: {},
    activeCount: 0,
    setConnected: (value: boolean) => set({ isConnected: value }),
    syncActiveJobs: (jobs: InventoryPrintJob[]) => {
      const nextActive: ActiveMap = {};
      jobs.forEach((job) => {
        if (isActiveStatus(job.status)) {
          nextActive[job.id] = true;
        }
      });
      set({
        activeJobs: nextActive,
        activeCount: Object.keys(nextActive).length,
      });
    },
    applyJobEvent: (job: InventoryPrintJob) => {
      set((state) => {
        const nextActive = { ...state.activeJobs };
        if (isActiveStatus(job.status)) {
          nextActive[job.id] = true;
        } else {
          delete nextActive[job.id];
        }

        const nextHighlights: HighlightMap = { ...state.highlightedJobs };
        if (job.status === 'completed' || job.status === 'failed') {
          nextHighlights[job.id] = Date.now();
        } else {
          delete nextHighlights[job.id];
        }

        return {
          activeJobs: nextActive,
          activeCount: Object.keys(nextActive).length,
          highlightedJobs: nextHighlights,
        };
      });
    },
    cleanupHighlights: () => {
      const now = Date.now();
      set((state) => {
        const nextEntries = Object.entries(state.highlightedJobs).filter(
          ([, timestamp]) => now - timestamp < HIGHLIGHT_TTL_MS,
        );
        if (nextEntries.length === Object.keys(state.highlightedJobs).length) {
          return state;
        }
        const nextHighlights: HighlightMap = {};
        nextEntries.forEach(([jobId, timestamp]) => {
          nextHighlights[jobId] = timestamp;
        });
        return {
          highlightedJobs: nextHighlights,
        };
      });
    },
  }),
);

export function usePrintQueueLiveState() {
  return useInventoryPrintQueueLiveStore((state) => ({
    isConnected: state.isConnected,
    activeCount: state.activeCount,
    highlightedJobs: state.highlightedJobs,
  }));
}

// Совместимость: временный алиас
export const selectPrintQueueLiveState = usePrintQueueLiveState;

